import { useState, useCallback } from 'react';

import { useUpdateSpeaker } from '@/api/mutations/useUpdateSpeaker';

interface LogicalSpeaker {
  id: string;
  name: string;
  color: string;
}

interface InternalSpeaker {
  id: string;
  internalName: string;
  sessionNumber: number;
  displayName: string;
  logicalSpeakerId?: string;
  logicalSpeakerName?: string;
  logicalSpeakerColor?: string;
}

export const useSpeakerManagement = (
  logicalSpeakers: LogicalSpeaker[],
  internalSpeakers: InternalSpeaker[],
  mapInternalSpeaker: (internalSpeakerId: string, logicalSpeakerName: string) => void,
  caseId: string | null,
  transcriptId: string | null,
) => {
  const [editingSpeaker, setEditingSpeaker] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const updateSpeakerMutation = useUpdateSpeaker();

  const startEditingSpeaker = useCallback((speaker: LogicalSpeaker | InternalSpeaker) => {
    setEditingSpeaker(speaker.id);
    setEditingName('name' in speaker ? speaker.name : '');
  }, []);

  const cancelEditingSpeaker = useCallback(() => {
    setEditingSpeaker(null);
    setEditingName('');
  }, []);

  const saveSpeakerMapping = useCallback(
    async (speakerId: string, name: string) => {
      if (!name.trim()) return;

      try {
        // Check if this is an internal speaker (needs mapping)
        const internalSpeaker = internalSpeakers.find((s) => s.id === speakerId);

        if (internalSpeaker) {
          // Map internal speaker to logical speaker
          mapInternalSpeaker(speakerId, name.trim());
        } else {
          // This is an existing logical speaker, update via API if needed
          const logicalSpeaker = logicalSpeakers.find((s) => s.id === speakerId);
          if (logicalSpeaker && caseId && transcriptId) {
            await updateSpeakerMutation.mutateAsync({
              caseId,
              transcriptId,
              speakerId,
              name: name.trim(),
            });
          }
        }

        setEditingSpeaker(null);
        setEditingName('');
      } catch {
        setEditingSpeaker(null);
        setEditingName('');
      }
    },
    [editingName, updateSpeakerMutation, logicalSpeakers, internalSpeakers, mapInternalSpeaker, caseId, transcriptId],
  );

  return {
    editingSpeaker,
    editingName,
    startEditingSpeaker,
    cancelEditingSpeaker,
    saveSpeakerMapping,
    setEditingName,
  };
};
